import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/axios';
import { formatIndianDate } from '../utils/format';
import ConfirmModal from './ConfirmModal';
import { useToast } from './ToastHost';

const ClientIssues = ({ user }) => {
  const toast = useToast();
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionBusy, setActionBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmInput, setConfirmInput] = useState(false);
  const [confirmInputLabel, setConfirmInputLabel] = useState('');
  const [confirmInputPlaceholder, setConfirmInputPlaceholder] = useState('');
  const confirmActionRef = useRef(null);

  const openConfirm = (title, message, action, opts = {}) => {
    confirmActionRef.current = action;
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmInput(Boolean(opts.input));
    setConfirmInputLabel(opts.inputLabel || '');
    setConfirmInputPlaceholder(opts.inputPlaceholder || '');
    setConfirmOpen(true);
  };

  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);

  const [projectFilter, setProjectFilter] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ project_id: '', title: '', message_text: '' });
  const [formFiles, setFormFiles] = useState(null);

  const [threadOpen, setThreadOpen] = useState({});
  const [threadState, setThreadState] = useState({});
  const [replyDraft, setReplyDraft] = useState({});
  const [replyFiles, setReplyFiles] = useState({});

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsIssue, setDetailsIssue] = useState(null);

  const openDetails = useCallback(async (issue) => {
    if (!issue) return;
    setDetailsIssue(issue);
    setDetailsOpen(true);
    await fetchThread(issue.id, 1);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
    setDetailsIssue(null);
  }, []);

  const initials = (name) => {
    const n = String(name || '').trim();
    if (!n) return 'U';
    const parts = n.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || 'U';
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return `${a}${b}`.toUpperCase();
  };

  const dayLabel = (d) => new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }).format(d);

  const threadItems = (items) => {
    const list = Array.isArray(items) ? items : [];
    const out = [];
    let lastKey = null;
    for (const m of list) {
      const ts = m.created_at ? new Date(m.created_at) : null;
      const key = ts && !Number.isNaN(ts.getTime()) ? `${ts.getFullYear()}-${ts.getMonth()}-${ts.getDate()}` : null;
      if (key && key !== lastKey) {
        out.push({ type: 'divider', key: `d-${key}`, label: dayLabel(ts) });
        lastKey = key;
      }
      out.push({ type: 'msg', key: `m-${m.id}`, msg: m });
    }
    return out;
  };

  const downloadAttachment = async (attachment) => {
    try {
      const res = await api.get(`/api/client-issues/attachments/${attachment.id}/download`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.original_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Error downloading attachment:', e);
    }
  };

  const canCreateIssue = useMemo(() => {
    return ['ADMIN', 'BDM', 'CLIENT'].includes(user?.role);
  }, [user?.role]);

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (nextPage = page, nextProjectId = projectFilter) => {
    try {
      setLoading(true);
      const [issuesRes, projectsRes] = await Promise.all([
        api.get('/api/client-issues', { params: { page: nextPage, limit, project_id: nextProjectId || undefined } }),
        api.get('/api/projects', { params: { all: true } })
      ]);

      const payload = issuesRes.data;
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        setIssues(payload.items);
        setTotal(payload.total || 0);
        setPage(payload.page || nextPage);
      } else {
        setIssues(Array.isArray(payload) ? payload : []);
        setTotal(Array.isArray(payload) ? payload.length : 0);
        setPage(nextPage);
      }

      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
    } catch (e) {
      console.error('Error fetching client issues:', e);
      setIssues([]);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, projectFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const createIssue = async (e) => {
    e.preventDefault();
    try {
      setActionBusy(true);
      const title = String(formData.title || '').trim();
      const messageText = String(formData.message_text || '').trim();
      const hasFiles = Boolean(formFiles && formFiles.length);

      if (messageText || hasFiles) {
        const fd = new FormData();
        fd.append('project_id', formData.project_id);
        fd.append('title', title);
        if (messageText) fd.append('message_text', messageText);
        if (hasFiles) {
          for (const f of formFiles) fd.append('files', f);
        }

        await api.post('/api/client-issues', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/api/client-issues', { project_id: formData.project_id, title });
      }

      setShowForm(false);
      setFormData({ project_id: '', title: '', message_text: '' });
      setFormFiles(null);
      await fetchData(1);
      toast.success('Issue created');
    } catch (e2) {
      console.error('Error creating issue:', e2);
      toast.error('Failed to create issue');
    } finally {
      setActionBusy(false);
    }
  };

  const fetchThread = async (issueId, nextPage = 1) => {
    try {
      const res = await api.get(`/api/client-issues/${issueId}/messages`, { params: { page: nextPage, limit: 10 } });
      const payload = res.data;
      setThreadState((prev) => ({
        ...prev,
        [issueId]: {
          items: payload.items || [],
          total: payload.total || 0,
          page: payload.page || nextPage,
          limit: payload.limit || 10
        }
      }));
    } catch (e) {
      console.error('Error loading issue thread:', e);
    }
  };

  const toggleThread = async (issueId) => {
    setThreadOpen((prev) => ({ ...prev, [issueId]: !prev[issueId] }));
    const currentlyOpen = Boolean(threadOpen[issueId]);
    if (!currentlyOpen) {
      await fetchThread(issueId, 1);
    }
  };

  const onFilesChange = (issueId, files) => {
    setReplyFiles((prev) => ({ ...prev, [issueId]: files }));
  };

  const postReply = async (issueId) => {
    const text = String(replyDraft[issueId] || '').trim();
    const files = replyFiles[issueId];
    if (!text && (!files || files.length === 0)) return;

    try {
      setActionBusy(true);
      const fd = new FormData();
      if (text) fd.append('message_text', text);
      if (files && files.length) {
        for (const f of files) fd.append('files', f);
      }

      await api.post(`/api/client-issues/${issueId}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReplyDraft((prev) => ({ ...prev, [issueId]: '' }));
      setReplyFiles((prev) => ({ ...prev, [issueId]: null }));
      await fetchThread(issueId, 1);
      toast.success('Reply sent');
    } catch (e) {
      console.error('Error posting reply:', e);
      toast.error('Failed to send reply');
    } finally {
      setActionBusy(false);
    }
  };

  const closeIssue = async (issueId) => {
    openConfirm('Close issue', 'Close this issue?', async () => {
      try {
        setActionBusy(true);
        await api.post(`/api/client-issues/${issueId}/close`);
        await fetchData(page);
        toast.success('Issue closed');
      } catch (e) {
        console.error('Error closing issue:', e);
        toast.error('Failed to close issue');
      } finally {
        setActionBusy(false);
      }
    });
  };

  const requestReopen = async (issueId) => {
    openConfirm(
      'Request reopen',
      'Provide a reason to reopen this issue.',
      async (reason) => {
        try {
          setActionBusy(true);
          await api.post(`/api/client-issues/${issueId}/reopen-request`, { reason: String(reason || '') });
          await fetchData(page);
          toast.success('Reopen requested');
        } catch (e) {
          console.error('Error requesting reopen:', e);
          toast.error('Failed to request reopen');
        } finally {
          setActionBusy(false);
        }
      },
      { input: true, inputLabel: 'Reason', inputPlaceholder: 'Optional reason...' }
    );
  };

  const approveReopen = async (issueId) => {
    openConfirm('Approve reopen', 'Approve reopen for this issue?', async () => {
      try {
        setActionBusy(true);
        await api.post(`/api/client-issues/${issueId}/reopen-approve`);
        await fetchData(page);
        toast.success('Reopen approved');
      } catch (e) {
        console.error('Error approving reopen:', e);
        toast.error('Failed to approve reopen');
      } finally {
        setActionBusy(false);
      }
    });
  };

  const denyReopen = async (issueId) => {
    openConfirm(
      'Deny reopen',
      'Deny reopen for this issue. Optionally add a reason.',
      async (decisionReason) => {
        try {
          setActionBusy(true);
          await api.post(`/api/client-issues/${issueId}/reopen-deny`, { decision_reason: String(decisionReason || '') });
          await fetchData(page);
          toast.success('Reopen denied');
        } catch (e) {
          console.error('Error denying reopen:', e);
          toast.error('Failed to deny reopen');
        } finally {
          setActionBusy(false);
        }
      },
      { input: true, inputLabel: 'Reason (optional)', inputPlaceholder: 'Optional...' }
    );
  };

  if (loading) {
    return <div className="text-center p-8">Loading issues...</div>;
  }

  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Client Issues</h1>
          <div className="text-sm text-tertiary">{total} total • {issues.filter((i) => i.status === 'OPEN').length} open</div>
        </div>
        <div className="flex gap-2 items-center">
          <select
            className="glass-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
          {canCreateIssue && (
            <button type="button" className="glass-button primary" onClick={() => setShowForm(true)} disabled={actionBusy || confirmBusy}>
              + New Issue
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Create Issue</h3>
              <button type="button" className="glass-button" onClick={() => setShowForm(false)} disabled={actionBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={createIssue} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Project</label>
                  <select name="project_id" value={formData.project_id} onChange={handleChange} className="glass-select w-full" required>
                    <option value="">Select Project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Issue Title</label>
                  <input name="title" value={formData.title} onChange={handleChange} className="glass-input w-full" required />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Initial Message (optional)</label>
                  <textarea
                    name="message_text"
                    value={formData.message_text}
                    onChange={handleChange}
                    className="glass-textarea w-full"
                    rows={3}
                    placeholder="Describe the issue details..."
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Attachments (optional)</label>
                  <input type="file" multiple onChange={(e) => setFormFiles(e.target.files)} />
                </div>

                <div className="modal-footer col-span-2">
                  <button type="button" className="glass-button" onClick={() => setShowForm(false)} disabled={actionBusy}>
                    Cancel
                  </button>
                  <button type="submit" className="glass-button primary" disabled={actionBusy}>
                    {actionBusy ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4">
        {issues.map((iss) => (
          <div
            key={iss.id}
            className="glass-card p-6"
            role="button"
            tabIndex={0}
            onClick={() => openDetails(iss)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openDetails(iss);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-lg font-semibold truncate">{iss.title}</div>
                  <span className={`status-badge ${iss.status === 'OPEN' ? 'approval-approved' : iss.status === 'REOPEN_PENDING' ? 'status-proposal' : 'approval-rejected'}`}>{iss.status}</span>
                </div>
                <div className="text-sm text-secondary mt-1">Project: {iss.project_title}</div>
                <div className="text-xs text-tertiary mt-1">Created: {formatIndianDate(iss.created_at)}</div>
              </div>

              <div className="flex gap-2 items-center flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
                {(user.role === 'ADMIN' || user.role === 'BDM' || user.role === 'CLIENT') && iss.status === 'OPEN' && (
                  <button type="button" className="glass-button danger text-sm px-3 py-1" onClick={(e) => { e.stopPropagation(); closeIssue(iss.id); }} disabled={actionBusy || confirmBusy}>
                    Close
                  </button>
                )}

                {user.role === 'CLIENT' && iss.status === 'CLOSED' && (
                  <button type="button" className="glass-button text-sm px-3 py-1" onClick={(e) => { e.stopPropagation(); requestReopen(iss.id); }} disabled={actionBusy || confirmBusy}>
                    Request Reopen
                  </button>
                )}

                {(user.role === 'ADMIN' || user.role === 'BDM') && iss.status === 'REOPEN_PENDING' && (
                  <>
                    <button type="button" className="glass-button success text-sm px-3 py-1" onClick={(e) => { e.stopPropagation(); approveReopen(iss.id); }} disabled={actionBusy || confirmBusy}>
                      Approve
                    </button>
                    <button type="button" className="glass-button danger text-sm px-3 py-1" onClick={(e) => { e.stopPropagation(); denyReopen(iss.id); }} disabled={actionBusy || confirmBusy}>
                      Deny
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">Page {page} of {totalPages} ({total} issues)</div>
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchData(page - 1)} disabled={!canPrev}>Prev</button>
            <button type="button" className="glass-button" onClick={() => fetchData(page + 1)} disabled={!canNext}>Next</button>
          </div>
        </div>
      )}

      {issues.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No issues found</p>
        </div>
      )}

      {detailsOpen && detailsIssue ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeDetails}>
          <div className="modal" style={{ width: 'min(980px, 100%)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Issue Details</h3>
              <button type="button" className="glass-button" onClick={closeDetails} disabled={actionBusy || confirmBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 14 }}>
                <div className="col-span-2">
                  <div className="text-xl font-semibold">{detailsIssue.title}</div>
                  <div className="text-sm text-secondary">Project: {detailsIssue.project_title || '—'}</div>
                </div>

                <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-sm font-semibold mb-2">Status</div>
                  <div style={{ marginTop: 6 }}>
                    <span className={`status-badge ${detailsIssue.status === 'OPEN' ? 'approval-approved' : detailsIssue.status === 'REOPEN_PENDING' ? 'status-proposal' : 'approval-rejected'}`}>
                      {detailsIssue.status}
                    </span>
                  </div>
                  <div className="text-xs text-tertiary" style={{ marginTop: 10 }}>Created: {formatIndianDate(detailsIssue.created_at)}</div>
                </div>

                <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-sm font-semibold mb-2">Project Info</div>
                  <div className="text-sm">
                    <div>
                      <span className="text-secondary">Project:</span>
                      <span className="ml-2 font-medium">{detailsIssue.project_title || '—'}</span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <span className="text-secondary">Issue ID:</span>
                      <span className="ml-2 font-medium">#{detailsIssue.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-sm font-semibold mb-2">Discussion</div>
              <div className="chat-panel">
                <div className="chat-thread">
                  {(() => {
                    const raw = threadState[detailsIssue.id]?.items || [];
                    if (raw.length === 0) {
                      return <div className="text-sm text-secondary">No messages yet</div>;
                    }
                    const items = threadItems(raw);
                    return (
                      <div className="chat-list">
                        {items.map((it) => {
                          if (it.type === 'divider') {
                            return (
                              <div key={it.key} className="chat-date-divider">
                                <span>{it.label}</span>
                              </div>
                            );
                          }
                          const m = it.msg;
                          const mine = String(m.created_by_user_id || '') === String(user.id)
                            || String(m.created_by_client_user_id || '') === String(user.id);
                          return (
                            <div key={it.key} className={`chat-row ${mine ? 'mine' : 'theirs'}`}>
                              <div className="chat-avatar" aria-hidden="true">{initials(m.created_by_name)}</div>
                              <div className="chat-bubble">
                                <div className="chat-meta">
                                  <div className="chat-name">{m.created_by_name || 'User'}</div>
                                  <span className={`role-pill role-${String(m.created_by_role || '').toLowerCase()}`}>{m.created_by_role}</span>
                                  <span className="chat-time">{formatIndianDate(m.created_at)}</span>
                                </div>
                                {m.message_text ? <div className="chat-text whitespace-pre-wrap">{m.message_text}</div> : null}
                                {Array.isArray(m.attachments) && m.attachments.length ? (
                                  <div className="chat-attachments">
                                    {m.attachments.map((a) => (
                                      <div key={a.id} className="chat-attachment">
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium truncate">{a.original_name}</div>
                                          <div className="text-xs text-tertiary">{a.mime_type || 'file'}</div>
                                        </div>
                                        <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => downloadAttachment(a)}>
                                          Download
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                {(detailsIssue.status === 'OPEN' || (user.role !== 'CLIENT' && detailsIssue.status === 'REOPEN_PENDING')) ? (
                  <div className="chat-composer" style={{ position: 'static' }}>
                    <div className="flex gap-2">
                      <input
                        className="glass-input w-full"
                        placeholder="Write a reply..."
                        value={replyDraft[detailsIssue.id] || ''}
                        onChange={(e) => setReplyDraft((prev) => ({ ...prev, [detailsIssue.id]: e.target.value }))}
                      />
                      <button type="button" className="glass-button primary" onClick={() => postReply(detailsIssue.id)} disabled={actionBusy || confirmBusy}>
                        Send
                      </button>
                    </div>
                    <div className="mt-2">
                      <input type="file" multiple onChange={(e) => onFilesChange(detailsIssue.id, e.target.files)} />
                    </div>
                  </div>
                ) : null}
              </div>

              {threadState[detailsIssue.id]?.total > 10 ? (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-tertiary">Page {threadState[detailsIssue.id]?.page || 1}</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="glass-button"
                      onClick={() => fetchThread(detailsIssue.id, Math.max((threadState[detailsIssue.id]?.page || 1) - 1, 1))}
                      disabled={(threadState[detailsIssue.id]?.page || 1) <= 1}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="glass-button"
                      onClick={() => fetchThread(detailsIssue.id, (threadState[detailsIssue.id]?.page || 1) + 1)}
                      disabled={(threadState[detailsIssue.id]?.items || []).length < 10}
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={closeDetails} disabled={actionBusy || confirmBusy}>
                  Close
                </button>
                {(user.role === 'ADMIN' || user.role === 'BDM' || user.role === 'CLIENT') && detailsIssue.status === 'OPEN' && (
                  <button type="button" className="glass-button danger" onClick={() => closeIssue(detailsIssue.id)} disabled={actionBusy || confirmBusy}>
                    Close Issue
                  </button>
                )}
                {user.role === 'CLIENT' && detailsIssue.status === 'CLOSED' && (
                  <button type="button" className="glass-button" onClick={() => requestReopen(detailsIssue.id)} disabled={actionBusy || confirmBusy}>
                    Request Reopen
                  </button>
                )}
                {(user.role === 'ADMIN' || user.role === 'BDM') && detailsIssue.status === 'REOPEN_PENDING' && (
                  <>
                    <button type="button" className="glass-button success" onClick={() => approveReopen(detailsIssue.id)} disabled={actionBusy || confirmBusy}>
                      Approve Reopen
                    </button>
                    <button type="button" className="glass-button danger" onClick={() => denyReopen(detailsIssue.id)} disabled={actionBusy || confirmBusy}>
                      Deny Reopen
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        busy={confirmBusy}
        input={confirmInput}
        inputLabel={confirmInputLabel}
        inputPlaceholder={confirmInputPlaceholder}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
        }}
        onConfirm={async (value) => {
          const action = confirmActionRef.current;
          if (!action) {
            setConfirmOpen(false);
            return;
          }
          try {
            setConfirmBusy(true);
            await action(value);
            setConfirmOpen(false);
          } finally {
            setConfirmBusy(false);
          }
        }}
      />
    </div>
  );
};

export default ClientIssues;
